import { useEffect, useState } from 'react'

/**
 * Detects whether the page is currently being machine-translated by the browser
 * (Chrome/Google Translate, Edge/Bing, Yandex).
 *
 * Google Translate — the Chrome built-in translation bar included — flags the
 * document by adding `translated-ltr`/`translated-rtl` to <html>, which is both
 * cheap and reliable to observe. Edge and Yandex leave per-element markers
 * instead, so those are polled for until translation is detected.
 *
 * Safari's translation leaves no documented marker, so it cannot be detected.
 */
const TRANSLATED_ROOT_CLASSES = ['translated-ltr', 'translated-rtl']

const TRANSLATION_MARKER_SELECTORS = [
  '#goog-gt-tt', // Google Translate tooltip container
  '.goog-te-banner-frame', // Google Translate website widget
  '[_msttexthash]', // Microsoft Edge / Bing translation
  'ya-tr-span' // Yandex Browser translation
]

const MARKER_POLL_INTERVAL_MS = 2000

export const detectBrowserTranslation = (): boolean => {
  if (typeof document === 'undefined') {
    return false
  }

  if (TRANSLATED_ROOT_CLASSES.some(className => document.documentElement.classList.contains(className))) {
    return true
  }

  return TRANSLATION_MARKER_SELECTORS.some(selector => document.querySelector(selector) !== null)
}

export const useBrowserTranslation = () => {
  const [isBrowserTranslationActive, setIsBrowserTranslationActive] = useState(detectBrowserTranslation)

  useEffect(() => {
    if (isBrowserTranslationActive || typeof document === 'undefined') {
      return undefined
    }

    const update = () => setIsBrowserTranslationActive(detectBrowserTranslation())

    const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : undefined

    observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    const intervalId = window.setInterval(update, MARKER_POLL_INTERVAL_MS)

    return () => {
      observer?.disconnect()
      window.clearInterval(intervalId)
    }
  }, [isBrowserTranslationActive])

  return { isBrowserTranslationActive }
}
